import { useParams, Link } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/base/ComicCard';
import { comics, categories } from '@/mocks/comics';

export default function ComicDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const comic = comics.find((c) => c.slug === slug);

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
              <i className="ri-file-unknow-line text-3xl"></i>
            </div>
            <h1 className="text-xl font-bold text-foreground-950">漫画未找到</h1>
            <p className="text-foreground-500 text-sm mt-2">该漫画可能已下架或链接错误</p>
            <Link to="/" className="mt-6 inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedComics = comics
    .filter((c) => c.slug !== comic.slug && c.categories.some((cat) => comic.categories.includes(cat)))
    .slice(0, 5);

  const comicCats = categories.filter((cat) => comic.categories.includes(cat.slug));

  const pageTitle = `${comic.title} - ${comic.author} - 韩国漫画`;
  const pageDesc = `${comic.title}，作者${comic.author}，${comic.status === '连载中' ? '连载至' + comic.chapters[0]?.number + '话' : '已完结'}。${comic.description}`;

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: comic.title,
    description: comic.description,
    author: { '@type': 'Person', name: comic.author },
    datePublished: comic.updatedAt,
    dateModified: comic.updatedAt,
    image: comic.cover,
    url: `http://aduvet.com/comic/${comic.slug}`,
    publisher: {
      '@type': 'Organization',
      name: '073漫画网',
    },
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'http://aduvet.com/' },
      { '@type': 'ListItem', position: 2, name: '漫画分类', item: 'http://aduvet.com/category/romance' },
      { '@type': 'ListItem', position: 3, name: comic.title, item: `http://aduvet.com/comic/${comic.slug}` },
    ],
  };

  return (
    <>
      <SEO
        title={pageTitle}
        description={pageDesc}
        keywords={`韩国漫画,${comic.title},${comic.author},无删减漫画,下拉式韩漫`}
        canonicalUrl={`http://aduvet.com/comic/${comic.slug}`}
        ogType="article"
        ogImage={comic.cover}
        jsonLd={{ '@context': 'https://schema.org', '@graph': [articleJsonLd, breadcrumbJsonLd] }}
      />

      <Navbar />

      <main className="pt-16">
        <nav className="bg-background-50 border-b border-background-200" aria-label="面包屑导航">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-3">
            <ol className="flex items-center gap-2 text-xs text-foreground-500 flex-wrap">
              <li><Link to="/" className="hover:text-primary-500 transition-colors">首页</Link></li>
              <li><i className="ri-arrow-right-s-line text-xs"></i></li>
              <li><Link to="/category/romance" className="hover:text-primary-500 transition-colors">漫画分类</Link></li>
              <li><i className="ri-arrow-right-s-line text-xs"></i></li>
              <li className="text-foreground-800 font-medium">{comic.title}</li>
            </ol>
          </div>
        </nav>

        <section className="bg-background-50 py-8 md:py-12">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col md:flex-row gap-8 md:gap-12">
              <div className="w-full md:w-[280px] lg:w-[320px] shrink-0">
                <div className="rounded-xl overflow-hidden">
                  <img
                    src={comic.cover}
                    alt={comic.title}
                    title={`${comic.title} - 韩国漫画`}
                    className="w-full aspect-[3/4] object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1">
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading">{comic.title}</h1>

                <div className="flex flex-wrap items-center gap-3 mt-3">
                  <span className="text-sm text-foreground-600">作者：<strong>{comic.author}</strong></span>
                  <span className="text-foreground-300">|</span>
                  <span className={`text-sm font-medium px-2.5 py-0.5 rounded-full font-label ${
                    comic.status === '连载中'
                      ? 'bg-accent-100 text-accent-700'
                      : 'bg-secondary-100 text-secondary-700'
                  }`}>
                    {comic.status}
                  </span>
                  <span className="text-foreground-300">|</span>
                  <span className="text-sm text-foreground-600 flex items-center gap-1">
                    <i className="ri-star-fill text-amber-400"></i>
                    <strong>{comic.rating}分</strong>
                  </span>
                  <span className="text-foreground-300">|</span>
                  <span className="text-sm text-foreground-600">
                    <i className="ri-eye-line mr-1"></i>
                    <strong>{(comic.views / 10000).toFixed(1)}万</strong>次阅读
                  </span>
                </div>

                <div className="flex flex-wrap gap-2 mt-3">
                  {comicCats.map((cat) => (
                    <Link
                      key={cat.slug}
                      to="/category/all"
                      className="px-3 py-1 bg-background-100 text-foreground-600 text-xs rounded-full hover:bg-primary-100 hover:text-primary-600 transition-colors whitespace-nowrap"
                    >
                      {cat.name}
                    </Link>
                  ))}
                </div>

                <div className="mt-6 p-4 md:p-5 bg-background-100 rounded-xl">
                  <h3 className="text-sm font-semibold text-foreground-800 mb-2">作品简介</h3>
                  <p className="text-foreground-600 text-sm leading-relaxed">{comic.description}</p>
                </div>

                <div className="mt-6 flex flex-wrap gap-3">
                  <button className="px-6 py-3 bg-primary-500 text-background-50 font-semibold rounded-full hover:bg-primary-600 transition-colors text-sm whitespace-nowrap cursor-pointer flex items-center gap-2">
                    <i className="ri-play-circle-line"></i>
                    开始阅读
                  </button>
                  <button className="px-6 py-3 bg-background-100 text-foreground-700 font-medium rounded-full hover:bg-background-200 transition-colors text-sm whitespace-nowrap cursor-pointer flex items-center gap-2">
                    <i className="ri-bookmark-line"></i>
                    收藏
                  </button>
                  <button className="px-6 py-3 bg-background-100 text-foreground-700 font-medium rounded-full hover:bg-background-200 transition-colors text-sm whitespace-nowrap cursor-pointer flex items-center gap-2">
                    <i className="ri-share-line"></i>
                    分享
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-background-100 py-10 md:py-14">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading">
                章节列表
                <span className="text-foreground-500 text-sm font-normal ml-2">
                  共{comic.chapters.length}话
                </span>
              </h2>
              <div className="flex items-center gap-2">
                <button className="px-3 py-1.5 text-xs font-medium bg-background-50 text-foreground-600 rounded-full hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap">
                  正序
                </button>
                <button className="px-3 py-1.5 text-xs font-medium bg-foreground-950 text-background-50 rounded-full cursor-pointer whitespace-nowrap">
                  倒序
                </button>
              </div>
            </div>

            <div className="bg-background-50 rounded-xl overflow-hidden">
              <div className="divide-y divide-background-200">
                {comic.chapters.map((chapter, index) => (
                  <button
                    key={chapter.id}
                    className="flex items-center justify-between px-4 md:px-6 py-3.5 hover:bg-background-100 transition-colors cursor-pointer group w-full text-left"
                  >
                    <div className="flex items-center gap-3 md:gap-4">
                      <span className="text-foreground-400 text-xs font-mono w-12 text-right font-label">
                        第{chapter.number}话
                      </span>
                      <span className="text-foreground-800 text-sm group-hover:text-primary-600 transition-colors font-medium">
                        {chapter.title}
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      {index === 0 && (
                        <span className="text-xs bg-accent-100 text-accent-700 px-2 py-0.5 rounded-full font-label whitespace-nowrap">
                          最新
                        </span>
                      )}
                      <span className="text-foreground-400 text-xs whitespace-nowrap">{chapter.updatedAt}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {comic.chapters.length > 5 && (
              <div className="mt-6 text-center">
                <button className="px-8 py-2.5 bg-background-50 text-foreground-600 font-medium rounded-full hover:bg-background-200 transition-colors text-sm cursor-pointer">
                  展开全部章节
                </button>
              </div>
            )}
          </div>
        </section>

        <section className="bg-background-50 py-10 md:py-14">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading">相关推荐</h2>
              <Link to="/category/romance" className="text-primary-500 text-sm hover:text-primary-600 transition-colors flex items-center gap-1 whitespace-nowrap">
                更多推荐
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5">
              {relatedComics.map((rc) => (
                <ComicCard
                  key={rc.slug}
                  slug={rc.slug}
                  title={rc.title}
                  author={rc.author}
                  cover={rc.cover}
                  rating={rc.rating}
                  status={rc.status}
                  latestChapter={rc.chapters[0]?.number}
                  variant="compact"
                />
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}